import React, {useCallback, useMemo, useState} from 'react';


import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';
import {getAvailableMembers} from 'src/openapi/admin/apis/GetAvailableAccountGroupMembersApi';
import {useAppDispatch} from 'src/store/hooks';

import {Autosuggest, AutosuggestItem, AutosuggestNoResults} from '@pb/kit-component-autosuggest-v2';
import Loading, {Types} from '@pb/kit-component-loading';

import {UserSuggestItem} from './user-suggest-item';
import {shouldAddToMembers, splitQuery} from './utils';

import type {ClipboardEvent} from 'react';
import type {Error} from 'src/openapi/admin/models/Error';
import type {MemberInfo} from 'src/openapi/admin/models/MemberInfo';
import type {UserShortInfo} from 'src/openapi/admin/models/UserShortInfo';

type Props = {
    accountId: number,
    memberIds: Array<number>,
    addedMembers: Array<MemberInfo>,
    onAdd: (user: MemberInfo) => void,
    onBulkAdd: (users: Array<MemberInfo>) => void,
    disabled?: boolean,
};

const INVALID_QUERY_ERROR = 'The search query contains invalid characters, please remove them and try again...';
const SEARCH_REGEX = /[^0-9 A-Za-z$&+,:{};_=?@#|'\]<>.^*()%!-\\/]/g;
const MAX_RESULTS = 30;

export const UserSuggest: React.FC<Props> = ({onAdd, accountId, memberIds, onBulkAdd, addedMembers, disabled}) => {
    const apiDispatch = useAppDispatch();

    const [searchedQuery, setQuery] = useState<string>('');
    const [suggestedData, setSuggestedData] = useState<Array<MemberInfo>>([]);
    const [shown, setShown] = useState(false);

    const [busyBulkLoad, setBusyBulkLoad] = useState(false);
    const [loadingError, setLoadingError] = useState<Error | null>(null);

    const sanitizedQuery = useMemo(
        () => searchedQuery.replace(SEARCH_REGEX, ''),
        [searchedQuery],
    );

    const loadOneUser = useCallback((query: string) => {
        setBusyBulkLoad(true);
        apiDispatch(getAvailableMembers({accountId, emails: [query]})).then(response => {
            if (response.payload.success) {
                setSuggestedData(response.payload.data
                    .slice(0, MAX_RESULTS)
                    .filter(({userId}) => !memberIds.includes(userId))
                    .filter(({email}) => !addedMembers.map(e => e.email).includes(email)),
                );
            } else {
                setLoadingError(response.payload.data);
            }
            setBusyBulkLoad(false);
        });
    }, [accountId, memberIds, addedMembers, apiDispatch]);

    useEffectWithDeps((prev, current) => {
        if (prev.sanitizedQuery !== current.sanitizedQuery) {
            if (current.sanitizedQuery && !busyBulkLoad) {
                loadOneUser(current.sanitizedQuery);
            } else {
                setSuggestedData([]);
            }
        }
    }, {sanitizedQuery});

    const bulkLoad = useCallback(async (e: ClipboardEvent) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text');
        const emails = splitQuery(text);

        if (!emails.length) {
            return;
        }

        setBusyBulkLoad(true);
        const bulkAddedMembersResponse = await apiDispatch(getAvailableMembers({accountId, emails}));
        setBusyBulkLoad(false);

        if (bulkAddedMembersResponse.payload.success) {
            onBulkAdd(bulkAddedMembersResponse.payload.data.filter((item: MemberInfo) => {
                return shouldAddToMembers(addedMembers, memberIds, item);
            }));
        }
    }, [addedMembers, accountId, apiDispatch, memberIds, onBulkAdd]);

    const clearSuggest = useCallback(() => {
        setSuggestedData([]);
        setQuery('');
    }, []);


    const onItemSelect = useCallback((data: MemberInfo) => {
        onAdd(data);
        clearSuggest();
    }, [clearSuggest, onAdd]);

    const autosuggestError = useMemo(() => {
        let error = null;

        if (sanitizedQuery !== searchedQuery) {
            error = INVALID_QUERY_ERROR;
        } else if (loadingError) {
            error = loadingError.message;
        } else if (busyBulkLoad) {
            error = 'loading...';
        }

        return error;
    }, [searchedQuery, loadingError, sanitizedQuery, busyBulkLoad]);

    const renderedSuggestions = useMemo(() => {
        if (autosuggestError) {
            return [
                <div key="error" style={{padding: 16}}>
                    {autosuggestError}
                </div>,
            ];
        } else if (suggestedData.length > 0) {
            return suggestedData.map(data => (
                <AutosuggestItem
                    key={data.userId}
                    value={data}
                    caption={<UserSuggestItem {...{data: data as unknown as UserShortInfo}}/>}
                    disabled={false}
                />


            ));
        } else if (searchedQuery.trim().length > 0 && !busyBulkLoad) {
            return [<AutosuggestNoResults key="no-results"/>];
        }
        return [];
    }, [suggestedData, autosuggestError, searchedQuery, busyBulkLoad]);

    return (
        <div onPaste={bulkLoad}>
            <Autosuggest
                disabled={disabled}
                value={searchedQuery}
                maxLength={255}
                shown={shown}
                setShown={setShown}
                onChange={e => setQuery(e.target.value)}
                onClear={clearSuggest}
                placeholder="Enter or paste email addresses"
                onItemSelect={onItemSelect}
                endAdornment={busyBulkLoad ? <Loading type={Types.ICON}/> : undefined}>
                {renderedSuggestions}
            </Autosuggest>
        </div>
    );
};
